import React from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { Text, Inline, Stack } from '@atlaskit/primitives';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { trackLinkClick } from '@atlassian/temp-analytics';
import { NoData, FormatCurrency } from '@css-xp/ppc-common';
import { FormattedDate } from '@css-xp/base';
import { Maybe } from '@css-xp/graphtypes/src/generated-types-sxp';
import { LineHeight } from './common-widget-item.styles';
import './widget-items.scss';

type Props = {
  // URL to redirect to when the user clicks on the number like Quote Number, Invoice Number, etc
  url: string;
  // Icon to display on the left side of the number like Quote Number, Invoice Number, etc
  icon: React.ReactNode;
  // Date when the item was created (like Quote created date)
  createdDate?: string;
  // Date when the item will expire (like Quote expiry date)
  expiryDate?: Maybe<string>;
  // Date when the item was generated (like Invoice generated date)
  generatedDate?: Maybe<string>;
  // Date when the item is due (like Invoice due date)
  dueDate?: Maybe<string>;
  // Test ID for the component
  testId?: string;
  // Heading for the widget item (Also used for widgetItemClick tracking)
  heading?: string;
  // Currency code for the total amount
  currency?: string;
  // Total amount for the item
  total?: number | string;
  // Left text to display on the widget item (like Quote Number, Invoice Number, etc)
  leftText?: React.ReactNode;
  // Company name to display on the widget item (left subtext)
  company?: React.ReactNode;
  // A generic right subtext to display on the widget item if no specific right subtext (generatedDate, etc.) is provided
  rightSubText?: React.ReactNode;
};
/**
 * Common component for Widget Item on the Home page for new design
 * @constructor
 */
const CommonWidgetItemComponent = ({
  url,
  leftText,
  company,
  rightSubText,
  icon,
  createdDate,
  expiryDate,
  currency,
  total,
  testId,
  heading = '',
  generatedDate,
  dueDate,
}: Props) => {
  const renderRightSubtext = () => {
    if (rightSubText) {
      return <>{rightSubText}</>;
    }

    if (!createdDate && !expiryDate && !generatedDate && !dueDate) {
      return <NoData testId="no-date" />;
    }

    if (dueDate) {
      return (
        <div className="content">
          <Text size="small" color="color.text.warning">
            Was due <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
          </Text>
        </div>
      );
    }

    if (generatedDate) {
      return (
        <div className="content">
          <Text size="small" color="color.text.subtlest">
            Generated <FormattedDate date={generatedDate} format={NEW_DATE_FORMAT} />
          </Text>
        </div>
      );
    }

    return (
      (!createdDate && !expiryDate) || (
        <div className="content">
          <Text size="small" color="color.text.subtlest">
            Created {createdDate ? <FormattedDate date={createdDate} format={NEW_DATE_FORMAT} /> : <NoData />} | Expires{' '}
            {expiryDate ? <FormattedDate date={expiryDate} format={NEW_DATE_FORMAT} /> : <NoData />}
          </Text>
        </div>
      )
    );
  };

  return (
    <LineHeight>
      <div className={classnames('flex align-center justify-space-between pv-12')} data-testid={testId}>
        <div className="left-side">
          <Inline alignBlock="center" space="space.100">
            {/*  Caveat: Using SVG as core icon package unavailable */}
            {/* @ts-ignore */}
            <img src={icon} height="24px" alt="icon" />
            <Stack>
              <div className="content">
                <Link
                  to={url}
                  onClick={() => {
                    trackLinkClick('widgetItemClick', { source: heading });
                  }}
                >
                  <div className="line-ellipsis title-black">{leftText}</div>
                </Link>
              </div>
              <div className="content">
                <div className="line-ellipsis">
                  <Text size="small" color="color.text.subtlest">
                    {company}
                  </Text>
                </div>
              </div>
            </Stack>
          </Inline>
        </div>
        <div className="text-align-right">
          {total ? (
            <div className="content">
              <FormatCurrency showCurrencyCode showCommas currency={currency} value={total} />
            </div>
          ) : (
            <NoData />
          )}
          {renderRightSubtext()}
        </div>
      </div>
    </LineHeight>
  );
};

export const CommonWidgetItem = React.memo(CommonWidgetItemComponent);
